import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { WindowService } from 'angular-v2-services';
import {
  BehaviorSubject,
  catchError,
  filter,
  fromEvent,
  map,
  Observable,
  of,
  Subscription,
  switchMap,
  tap,
  timer,
} from 'rxjs';

import { environment } from '../../environments/environment';

export interface IMyGuestUnreadCountResponse {
  count: number;
}

const POLL_INTERVAL_MS = 30_000;
const MY_CHAT_ROUTE = '/mychat';
const UNREAD_COUNT_URL = `${environment.api}/visitors-area/public/my-guest/unread-count`;

@Injectable({
  providedIn: 'root',
})
export class MyGuestUnreadCountService implements OnDestroy {
  private readonly countSubject = new BehaviorSubject<number>(0);
  readonly count$ = this.countSubject.asObservable();
  private pollingSubscription?: Subscription;
  private lifecycleSubscription?: Subscription;
  private canPoll = true;
  private isMyChatRoute = false;
  private isChatOpen = false;

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
    private readonly windowService: WindowService,
  ) {
    this.isMyChatRoute = this.router.url.startsWith(MY_CHAT_ROUTE);
    this.#initLifecycleListeners();
  }

  start(): void {
    if (this.pollingSubscription) {
      return;
    }
    this.pollingSubscription = timer(0, POLL_INTERVAL_MS)
      .pipe(
        filter(() => this.canPoll && !this.#isChatActive()),
        switchMap(() => this.#fetchCount()),
      )
      .subscribe();
  }

  stop(): void {
    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = undefined;
    this.countSubject.next(0);
  }

  /**
   * Pause unread polling while the Concierge panel (or equivalent) is open.
   */
  setChatOpen(isOpen: boolean): void {
    const wasOpen = this.isChatOpen;
    this.isChatOpen = isOpen;
    if (isOpen) {
      this.countSubject.next(0);
      return;
    }
    if (wasOpen && this.canPoll && !this.isMyChatRoute && this.pollingSubscription) {
      this.#fetchCount().subscribe();
    }
  }

  ngOnDestroy(): void {
    this.stop();
    this.lifecycleSubscription?.unsubscribe();
  }

  #isChatActive(): boolean {
    return this.isMyChatRoute || this.isChatOpen;
  }

  #initLifecycleListeners(): void {
    this.lifecycleSubscription = new Subscription();
    this.lifecycleSubscription.add(
      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe(event => {
          const wasMyChatRoute = this.isMyChatRoute;
          this.isMyChatRoute = event.urlAfterRedirects.startsWith(MY_CHAT_ROUTE);
          if (this.isMyChatRoute) {
            this.countSubject.next(0);
            return;
          }
          if (wasMyChatRoute && this.canPoll && !this.isChatOpen && this.pollingSubscription) {
            this.#fetchCount().subscribe();
          }
        }),
    );
    const document = this.windowService.document;
    if (!document) {
      return;
    }
    this.canPoll = document.visibilityState === 'visible';
    this.lifecycleSubscription.add(
      fromEvent(document, 'visibilitychange').subscribe(() => {
        this.canPoll = document.visibilityState === 'visible';
        if (this.canPoll && !this.#isChatActive() && this.pollingSubscription) {
          this.#fetchCount().subscribe();
        }
      }),
    );
  }

  #fetchCount(): Observable<void> {
    return this.http.get<IMyGuestUnreadCountResponse>(UNREAD_COUNT_URL).pipe(
      map(response => this.#normalizeCount(response.count)),
      catchError(() => of(0)),
      tap(count => this.countSubject.next(count)),
      map(() => undefined),
    );
  }

  #normalizeCount(value: unknown): number {
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
      return 0;
    }
    return Math.floor(value);
  }
}
